import React from "react";
import { Link } from "react-router-dom";
import {
  Scale,
  ArrowLeft,
  Lock,
  Database,
  FileLock2,
  Users,
  CreditCard,
  KeyRound,
  ShieldAlert,
  UserCheck,
  ArrowRight,
} from "lucide-react";
import { LandingFooter } from "@/components/landing/CtaBanner";

const CONTACT_EMAIL = "supravat111@gmail.com";

const TOC = [
  ["overview", "1. Overview"],
  ["access", "2. Sign-in and access control"],
  ["isolation", "3. Organisation-level isolation"],
  ["passwords", "4. Password handling"],
  ["documents", "5. Documents & object storage"],
  ["database", "6. Application database"],
  ["portal", "7. Client portal"],
  ["payments", "8. Payments & billing"],
  ["transport", "9. Transport security"],
  ["backups", "10. Backups & availability"],
  ["operations", "11. Infrastructure access"],
  ["responsibilities", "12. Your account responsibilities"],
  ["incidents", "13. Reporting a concern"],
  ["not-claiming", "14. What we do not claim"],
];

const HIGHLIGHTS = [
  {
    icon: Lock,
    title: "Private by default",
    body:
      "Documents are stored in private object storage and are not served through public links. Each download is authorised against your signed-in account.",
  },
  {
    icon: Users,
    title: "One firm, one silo",
    body:
      "Every record is scoped to your organisation. Application APIs are answered only from within your organisation, so one firm's data is not returned to another firm's users.",
  },
  {
    icon: KeyRound,
    title: "Passwords are hashed",
    body:
      "Passwords are stored only as bcrypt hashes. Nobody operating the service can read your password back — a forgotten password must be reset, not retrieved.",
  },
  {
    icon: CreditCard,
    title: "Payments handled by Razorpay",
    body:
      "Card numbers, UPI PINs, CVV and net banking credentials are entered into Razorpay's checkout, not into Advocate Diary. We never store them.",
  },
];

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`security-section-${id}`} className="scroll-mt-24">
    <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">{title}</h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="ml-1 space-y-2.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span aria-hidden="true" className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export default function SecurityPage() {
  return (
    <div data-testid="security-page" className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="security-brand" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
              <Scale className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl tracking-tight text-navy">Advocate Diary</span>
          </Link>
          <Link
            to="/"
            data-testid="security-back-home"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy/70 transition-colors hover:bg-secondary hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="border-b border-border bg-navy px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold">Trust</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Data, Storage &amp; Security
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
            How Advocate Diary handles your firm&rsquo;s cases, clients and documents — described in plain
            terms, without claims we haven&rsquo;t verified.
          </p>
        </div>
      </div>

      {/* Highlights */}
      <section className="border-b border-border bg-secondary/40 px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((h) => {
            const Icon = h.icon;
            return (
              <div
                key={h.title}
                data-testid={`security-highlight-${h.title.toLowerCase().split(/\s+/).slice(0, 2).join("-")}`}
                className="rounded-xl border border-border bg-white p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-navy">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <main className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[220px_minmax(0,1fr)]">
          <nav data-testid="security-toc" className="hidden lg:block">
            <div className="sticky top-24">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                On this page
              </p>
              <ul className="mt-4 space-y-2 border-l border-border pl-4">
                {TOC.map(([id, label]) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      data-testid={`security-toc-${id}`}
                      className="block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-l border-border pl-4">
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                  Related
                </p>
                <Link
                  to="/privacy-policy"
                  data-testid="security-related-privacy"
                  className="mt-3 block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  data-testid="security-related-terms"
                  className="mt-2 block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/refund-policy"
                  data-testid="security-related-refund"
                  className="mt-2 block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                >
                  Cancellation &amp; Refund Policy
                </Link>
                <Link
                  to="/contact"
                  data-testid="security-related-contact"
                  className="mt-2 block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                >
                  Contact &amp; Support
                </Link>
              </div>
            </div>
          </nav>

          <article className="max-w-3xl space-y-12">
            <Section id="overview" title="1. Overview">
              <p>
                Advocate Diary is a software-as-a-service application used by advocates, chambers and law
                firms in India to manage their practice — cases, clients, hearings, tasks, notifications and
                related records, and to upload documents that go with a matter.
              </p>
              <p>
                This page is a plain description of how the application handles that information: how access
                is controlled, where documents and application data are held, how payments are processed, and
                what stays your responsibility. It is written to be accurate rather than impressive, and it
                does not use claims we cannot back up.
              </p>
            </Section>

            <Section id="access" title="2. Sign-in and access control">
              <p>
                Every request against the application, other than the handful of clearly public pages, is
                authenticated. There are no public read endpoints for firm data. A signed-in user is placed in
                a specific role — firm administrator, advocate, staff, client (portal) or platform
                administrator — and the role determines what they can see and do.
              </p>
              <Bullets
                items={[
                  "Sign-in tokens are scoped to the individual account and are invalidated when you sign out.",
                  "Repeated failed sign-in attempts are rate-limited both by identifier and by IP address, to make guessing attacks harder.",
                  "Password reset links are single-use, expire after one hour, and are stored only as hashes.",
                  "Platform administration is fully separate from firm accounts and cannot reach a firm's records; equally, firm users cannot reach platform administration.",
                ]}
              />
            </Section>

            <Section id="isolation" title="3. Organisation-level isolation">
              <p>
                Advocate Diary is built so that each organisation&rsquo;s records are scoped to that
                organisation. Every case, client, hearing, task, document, note and report belongs to an
                organisation, and application APIs answer only from within it.
              </p>
              <Bullets
                items={[
                  "One firm's users cannot list, open, edit or export another firm's data through the application's APIs.",
                  "Within a firm, what a member can do depends on the role assigned to them — for example only a firm administrator can manage the team, and only staff-level users can create new matters.",
                  "The client (portal) role can see only their own matter, and only the updates the firm chooses to publish.",
                ]}
              />
            </Section>

            <Section id="passwords" title="4. Password handling">
              <p>
                Passwords are never stored in a readable form. When you set a password, it is converted into a
                one-way hash using <strong className="text-navy">bcrypt</strong>, and only that hash is stored.
                Nobody operating the service can read your password back, which is also why a forgotten
                password must be reset rather than retrieved.
              </p>
              <Bullets
                items={[
                  "Passwords must be at least eight characters.",
                  "Password reset tokens are randomly generated, stored only as hashes, are single-use, and expire.",
                  "Reset links only work over HTTPS.",
                ]}
              />
            </Section>

            <Section id="documents" title="5. Documents & object storage">
              <p>
                Documents you upload — pleadings, orders, briefs, correspondence and other files attached to
                your matters — are stored in <strong className="text-navy">Cloudflare R2</strong>, a private
                object storage service. The R2 bucket used by Advocate Diary is not public.
              </p>
              <Bullets
                items={[
                  "Documents are not served through public links. There are no permanent public URLs for your files.",
                  "Every download goes through an authenticated endpoint on the application, which checks that the signed-in user belongs to the organisation the document belongs to and has the role required to see it.",
                  "Only the file itself is stored in the bucket; the record of what the file is (name, matter, uploader, size) is stored in the application database and is also scoped by organisation.",
                  "When you delete a document in the application, it is marked as deleted and is no longer available through the application.",
                ]}
              />
              <p>
                Access to R2 from the application uses infrastructure credentials that are held only on the
                server side. Those credentials are never sent to the browser or exposed in any application
                response.
              </p>
            </Section>

            <Section id="database" title="6. Application database">
              <p>
                The application&rsquo;s records — organisations, users, cases, clients, hearings, tasks,
                subscriptions, and the metadata that describes uploaded documents — are stored in a managed
                MongoDB cloud database used by the service. Access to the database is over an authenticated
                connection and is restricted to the application server.
              </p>
              <Bullets
                items={[
                  "The application does not expose the database directly to the browser or to any third party.",
                  "Database credentials are held only on the server side and are never sent to the browser or included in any application response.",
                  "The application relies on database-level unique indexes to prevent duplicate subscriptions, duplicate payment records and duplicate webhook events, so that billing state stays consistent.",
                ]}
              />
            </Section>

            <Section id="portal" title="7. Client portal">
              <p>
                A firm can invite a client to a read-only portal for a matter that firm handles for them.
                Portal access is not open — it is created only by an authenticated invitation from the firm.
              </p>
              <Bullets
                items={[
                  "An invitation is stored as an invitation record with an expiry, and the invitee sets their own password when they accept it.",
                  "A client account is a distinct account type (the client role) linked to a specific client record, and it cannot see the firm's internal notes, other clients, other matters or firm-wide data.",
                  "The firm chooses what to publish to the portal — the client sees only the updates the firm makes visible.",
                  "Revoking a client's access to the portal is done from within the application by the firm.",
                ]}
              />
            </Section>

            <Section id="payments" title="8. Payments & billing">
              <p>
                Paid subscriptions are processed through <strong className="text-navy">Razorpay</strong>. When
                you subscribe, checkout runs on Razorpay and returns you to Advocate Diary on completion.
                Razorpay handles the payment transaction itself.
              </p>
              <Bullets
                items={[
                  "Advocate Diary does not store card numbers, CVV, UPI IDs, UPI PINs, net banking credentials or any other payment instrument credentials. These are entered into and handled by Razorpay.",
                  "Razorpay's webhook notifications for your subscription are verified against a shared signing secret before they are processed, so unsigned or forged notifications are rejected.",
                  "The application stores subscription and payment records returned by the gateway — plan, status, gateway reference IDs, payment amount and date — so that your account, receipts and access are correct.",
                  "Razorpay's own handling of your payment details is governed by Razorpay's terms and privacy policy.",
                ]}
              />
            </Section>

            <Section id="transport" title="9. Transport security">
              <p>
                The application is served over HTTPS. Sign-in, application traffic, document uploads and
                downloads, password resets and payment redirects all travel over encrypted connections
                between your browser and our servers.
              </p>
              <p>
                Password reset links refuse to be issued unless the configured application base URL is HTTPS,
                so a reset link is never emitted over a plain-text URL.
              </p>
            </Section>

            <Section id="backups" title="10. Backups & availability">
              <p>
                Advocate Diary is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. We
                do our reasonable best to keep the service available, but we do not publish a fixed uptime
                percentage or a guaranteed availability commitment, because that is not something we have
                committed to.
              </p>
              <p>
                We do not claim zero data loss. Backups and disaster recovery capabilities depend on the
                database and storage services used to run the application; we do not publish a specific
                recovery-point or recovery-time objective, because none has been established for the service.
                You remain responsible for keeping your own records of matters, dates and deadlines that are
                important to you.
              </p>
            </Section>

            <Section id="operations" title="11. Infrastructure access">
              <p>
                Access to the servers, database and storage that run Advocate Diary is restricted to the
                small set of systems and people that operate the service, using authenticated credentials
                that are not shared with third parties.
              </p>
              <Bullets
                items={[
                  "Infrastructure credentials (database, storage, payment gateway, email) are stored as server-side environment secrets and are never sent to the browser or included in any application response.",
                  "We do not publish specific infrastructure details, connection strings, bucket names or key material — they are not part of what a user of the service needs to know, and disclosing them would reduce security.",
                  "Third-party services used by the application (Cloudflare R2 for document storage, Razorpay for payments, an email delivery provider, cloud hosting) receive only what they need to perform their function.",
                ]}
              />
            </Section>

            <Section id="responsibilities" title="12. Your account responsibilities">
              <p>
                Some things stay in your hands. You are responsible for:
              </p>
              <Bullets
                items={[
                  "Keeping your password confidential and not sharing your sign-in credentials — each team member should have their own account.",
                  "Choosing a strong password (at least eight characters) and not reusing one from another service.",
                  "Signing out on devices that are not yours, including shared devices and court-room devices.",
                  "Giving the correct role to each team member you add, and removing access when someone leaves.",
                  "Inviting a client to the portal only at an email address you have verified with that client, and revoking access when it is no longer appropriate.",
                  "Meeting your own professional and confidentiality obligations towards your clients, including deciding what to record about a matter.",
                ]}
              />
              <p>
                If you believe your account has been accessed without authorisation, contact us at{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="security-inline-contact"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  {CONTACT_EMAIL}
                </a>{" "}
                so we can help.
              </p>
            </Section>

            <Section id="incidents" title="13. Reporting a concern">
              <p>
                If you find a security issue in Advocate Diary — for example a way to see data outside your
                own organisation, a way to reach a document without being signed in, or a way to bypass a
                permission — please tell us at{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="security-inline-report"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  {CONTACT_EMAIL}
                </a>
                . Please include enough detail for us to reproduce it, and please do not publish it before we
                have had a reasonable chance to fix it.
              </p>
              <p>
                We do not publish a specific response-time or fix-time commitment for security reports,
                because we have not made one — but we do take reports seriously and reply from a person.
              </p>
            </Section>

            <Section id="not-claiming" title="14. What we do not claim">
              <p>
                In the interest of being accurate rather than impressive, Advocate Diary makes{" "}
                <strong className="text-navy">no claim</strong> to any of the following:
              </p>
              <Bullets
                items={[
                  "ISO/IEC 27001, SOC 2 or PCI DSS certification for Advocate Diary. If certification is ever obtained, this page will be updated.",
                  "End-to-end encryption of application data (traffic between your browser and our servers is over HTTPS; that is not the same thing as end-to-end encryption).",
                  "Zero data loss, or absolute protection from breaches, or immunity to service disruptions.",
                  "A guaranteed uptime percentage or a published service-level agreement.",
                  "Any \"military-grade\" or similar marketing security label.",
                  "Any compliance certification that has not been independently verified.",
                ]}
              />
              <p>
                We prefer to describe what the application actually does — organisation-scoped access,
                authenticated document downloads, hashed passwords, Razorpay-handled payments, signed webhooks
                — and to update this page as the service changes, rather than to claim more.
              </p>
            </Section>

            <div className="rounded-lg border border-border bg-secondary/60 p-5">
              <p className="text-xs leading-relaxed text-muted-foreground">
                For a description of what information the application collects and how it is used, see our{" "}
                <Link
                  to="/privacy-policy"
                  data-testid="security-footer-privacy"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Privacy Policy
                </Link>
                . For the terms that apply to your use of the service, see our{" "}
                <Link
                  to="/terms"
                  data-testid="security-footer-terms"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Terms of Service
                </Link>
                . Cancellations and refunds are covered by our{" "}
                <Link
                  to="/refund-policy"
                  data-testid="security-footer-refund"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Cancellation &amp; Refund Policy
                </Link>
                . For anything else,{" "}
                <Link
                  to="/contact"
                  data-testid="security-footer-contact"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  contact us
                </Link>
                .
              </p>
            </div>
          </article>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
